import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowsClockwise, MagnifyingGlass, PencilSimple, Plus, Star, X } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import KitModal from '../../components/UI/KitModal';
import AdminAiModelsService, {
  AiModelTag,
  CreateModelPricePayload,
  ModelPriceFilters,
  ModelPriceResponse,
  SystemAiModelResponse,
} from '../../service/AdminAiModelsService';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';

const PAGE_SIZE = 25;

const MODEL_TYPES = ['Text', 'Image', 'Audio', 'Video', 'Other'];

const AI_COMPANIES = [
  'anthropic',
  'buildWithApi',
  'censusData',
  'clipDrop',
  'evenLabs',
  'fal',
  'gemini',
  'google',
  'googleSheets',
  'groq',
  'ideogram',
  'mistral',
  'openAi',
  'palm',
  'perplexity',
  'playHt',
  'replicate',
  'runwayMl',
  'secData',
  'semRush',
  'serpAPi',
  'stabilityAi',
  'summarize',
  'webScraper',
  'xai',
];

type AiModelMode = 'none' | 'create' | 'existing';

const EMPTY_CREATE_FORM: CreateModelPricePayload = {
  model: '',
  fluxConversion: 0,
  aiCompany: '',
  modelName: '',
  modelType: 'chat',
  maxInputToken: null,
  maxOutputToken: null,
  contextWindow: null,
  isVisionModel: false,
  supportsReasoning: false,
  reasoningAlwaysOn: false,
  supportsWebSearch: false,
  supportsStreaming: false,
  isActive: true,
  isDeprecated: false,
  description: '',
  displayName: '',
  tags: [],
};

const TagsEditor: React.FC<{
  tags: AiModelTag[];
  onChange: (tags: AiModelTag[]) => void;
}> = ({ tags, onChange }) => {
  const [newLabel, setNewLabel] = React.useState('');
  const [newTooltip, setNewTooltip] = React.useState('');
  const [editIndex, setEditIndex] = React.useState<number | null>(null);
  const [editLabel, setEditLabel] = React.useState('');
  const [editTooltip, setEditTooltip] = React.useState('');

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    onChange([...tags, { label, tooltip: newTooltip.trim() }]);
    setNewLabel('');
    setNewTooltip('');
  };

  const handleRemove = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
    if (editIndex === index) setEditIndex(null);
  };

  const handleStartEdit = (index: number) => {
    setEditIndex(index);
    setEditLabel(tags[index].label);
    setEditTooltip(tags[index].tooltip);
  };

  const handleSaveEdit = () => {
    if (editIndex === null) return;
    const label = editLabel.trim();
    if (!label) return;
    const updated = [...tags];
    updated[editIndex] = { label, tooltip: editTooltip.trim() };
    onChange(updated);
    setEditIndex(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Tags
      </Typography>

      {tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {tags.map((tag, i) => (
            <Chip
              key={i}
              label={
                <Tooltip title={tag.tooltip || '(no tooltip)'} arrow>
                  <span>{tag.label}</span>
                </Tooltip>
              }
              size="small"
              variant="outlined"
              onDelete={() => handleRemove(i)}
              deleteIcon={<X size={14} />}
              onClick={() => handleStartEdit(i)}
              icon={<PencilSimple size={14} />}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      )}

      {editIndex !== null ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            label="Label"
            size="small"
            value={editLabel}
            onChange={e => setEditLabel(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Tooltip"
            size="small"
            value={editTooltip}
            onChange={e => setEditTooltip(e.target.value)}
            sx={{ flex: 2 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveEdit}
            disabled={!editLabel.trim()}
            sx={{ minWidth: 70, height: 40 }}
          >
            Update
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditIndex(null)}
            sx={{ minWidth: 70, height: 40 }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            label="Label"
            size="small"
            placeholder="e.g. Fast"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Tooltip"
            size="small"
            placeholder="e.g. Optimized for speed"
            value={newTooltip}
            onChange={e => setNewTooltip(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            sx={{ flex: 2 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleAdd}
            disabled={!newLabel.trim()}
            startIcon={<Plus size={14} />}
            sx={{ minWidth: 80, height: 40 }}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

const AiModelsManagementPage: React.FC = () => {
  const { setNotification } = useNotificationStore();

  const [models, setModels] = useState<ModelPriceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterDeprecated, setFilterDeprecated] = useState<boolean | null>(null);
  const [filterVision, setFilterVision] = useState<boolean | null>(null);
  const [filterReasoning, setFilterReasoning] = useState<boolean | null>(null);
  const [filterReasoningAlways, setFilterReasoningAlways] = useState<boolean | null>(null);
  const [filterWebSearch, setFilterWebSearch] = useState<boolean | null>(null);
  const [filterStreaming, setFilterStreaming] = useState<boolean | null>(null);
  const [filterModelType, setFilterModelType] = useState('');

  const [selectedModel, setSelectedModel] = useState<ModelPriceResponse | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editFluxConversion, setEditFluxConversion] = useState<number>(0);
  const [editMaxOutputToken, setEditMaxOutputToken] = useState<number>(0);
  const [editIsActive, setEditIsActive] = useState(false);
  const [editIsDeprecated, setEditIsDeprecated] = useState(false);
  const [editIsVisionModel, setEditIsVisionModel] = useState(false);
  const [editSupportsReasoning, setEditSupportsReasoning] = useState(false);
  const [editReasoningAlwaysOn, setEditReasoningAlwaysOn] = useState(false);
  const [editSupportsWebSearch, setEditSupportsWebSearch] = useState(false);
  const [editSupportsStreaming, setEditSupportsStreaming] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editContextWindow, setEditContextWindow] = useState<number | null>(null);
  const [editMaxInputToken, setEditMaxInputToken] = useState<number | null>(null);
  const [editTags, setEditTags] = useState<AiModelTag[]>([]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [aiModelMode, setAiModelMode] = useState<AiModelMode>('create');
  const [createForm, setCreateForm] =
    useState<CreateModelPricePayload>(EMPTY_CREATE_FORM);

  const [existingModelSearch, setExistingModelSearch] = useState('');
  const [existingModelOptions, setExistingModelOptions] = useState<SystemAiModelResponse[]>([]);
  const [selectedExistingModel, setSelectedExistingModel] = useState<SystemAiModelResponse | null>(null);
  const [searchingModels, setSearchingModels] = useState(false);

  const [editAiModelMode, setEditAiModelMode] = useState<AiModelMode>('none');
  const [editNewAiCompany, setEditNewAiCompany] = useState('');
  const [editNewModelName, setEditNewModelName] = useState('');
  const [editNewModelType, setEditNewModelType] = useState('Text');
  const [editNewMaxInputToken, setEditNewMaxInputToken] = useState<number | null>(null);
  const [editNewMaxOutputToken, setEditNewMaxOutputToken] = useState<number | null>(null);
  const [editNewContextWindow, setEditNewContextWindow] = useState<number | null>(null);
  const [editNewIsVisionModel, setEditNewIsVisionModel] = useState(false);
  const [editNewSupportsReasoning, setEditNewSupportsReasoning] = useState(false);
  const [editNewReasoningAlwaysOn, setEditNewReasoningAlwaysOn] = useState(false);
  const [editNewSupportsWebSearch, setEditNewSupportsWebSearch] = useState(false);
  const [editNewSupportsStreaming, setEditNewSupportsStreaming] = useState(false);
  const [editNewIsActive, setEditNewIsActive] = useState(true);
  const [editNewIsDeprecated, setEditNewIsDeprecated] = useState(false);
  const [editNewDescription, setEditNewDescription] = useState('');
  const [editNewDisplayName, setEditNewDisplayName] = useState('');
  const [editNewTags, setEditNewTags] = useState<AiModelTag[]>([]);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const existingSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [filterActive, filterDeprecated, filterVision, filterReasoning, filterReasoningAlways, filterWebSearch, filterStreaming, filterModelType]);

  useEffect(() => {
    if (existingSearchRef.current) clearTimeout(existingSearchRef.current);
    existingSearchRef.current = setTimeout(async () => {
      if (aiModelMode !== 'existing' && editAiModelMode !== 'existing') return;
      setSearchingModels(true);
      try {
        const results = await AdminAiModelsService.searchSystemAiModels(
          existingModelSearch,
        );
        setExistingModelOptions(Array.isArray(results) ? results : []);
      } catch {
        setExistingModelOptions([]);
      } finally {
        setSearchingModels(false);
      }
    }, 300);
    return () => {
      if (existingSearchRef.current) clearTimeout(existingSearchRef.current);
    };
  }, [existingModelSearch, aiModelMode, editAiModelMode]);

  const filters: ModelPriceFilters = {
    search: debouncedSearch,
    isActive: filterActive,
    isDeprecated: filterDeprecated,
    isVisionModel: filterVision,
    supportsReasoning: filterReasoning,
    reasoningAlwaysOn: filterReasoningAlways,
    supportsWebSearch: filterWebSearch,
    supportsStreaming: filterStreaming,
    modelType: filterModelType,
  };

  const fetchModels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminAiModelsService.getAll(
        page + 1,
        PAGE_SIZE,
        filters,
      );
      setModels(response.data);
      setTotal(response.meta.total);
    } catch (error: any) {
      setNotification({
        type: NotificationType.Error,
        message: error?.message || 'Failed to load model prices',
      });
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterActive, filterDeprecated, filterVision, filterReasoning, filterReasoningAlways, filterWebSearch, filterStreaming, filterModelType, setNotification]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleOpenModal = (model: ModelPriceResponse) => {
    setSelectedModel(model);
    setEditFluxConversion(model.fluxConversion);
    if (model.systemAiModel) {
      setEditMaxOutputToken(model.systemAiModel.maxOutputToken ?? 0);
      setEditMaxInputToken(model.systemAiModel.maxInputToken ?? null);
      setEditContextWindow(model.systemAiModel.contextWindow ?? null);
      setEditIsActive(model.systemAiModel.isActive);
      setEditIsDeprecated(model.systemAiModel.isDeprecated);
      setEditIsVisionModel(model.systemAiModel.isVisionModel);
      setEditSupportsReasoning(model.systemAiModel.supportsReasoning);
      setEditReasoningAlwaysOn(model.systemAiModel.reasoningAlwaysOn);
      setEditSupportsWebSearch(model.systemAiModel.supportsWebSearch);
      setEditSupportsStreaming(model.systemAiModel.supportsStreaming);
      setEditDisplayName(model.systemAiModel.displayName ?? '');
      setEditTags(model.systemAiModel.tags ?? []);
    } else {
      setEditMaxOutputToken(0);
      setEditMaxInputToken(null);
      setEditContextWindow(null);
      setEditIsActive(false);
      setEditIsDeprecated(false);
      setEditIsVisionModel(false);
      setEditSupportsReasoning(false);
      setEditReasoningAlwaysOn(false);
      setEditSupportsWebSearch(false);
      setEditSupportsStreaming(false);
      setEditDisplayName('');
      setEditTags([]);
    }
    setEditAiModelMode('none');
    setEditNewAiCompany('');
    setEditNewModelName('');
    setEditNewModelType('Text');
    setEditNewMaxInputToken(null);
    setEditNewMaxOutputToken(null);
    setEditNewContextWindow(null);
    setEditNewIsVisionModel(false);
    setEditNewSupportsReasoning(false);
    setEditNewReasoningAlwaysOn(false);
    setEditNewSupportsWebSearch(false);
    setEditNewSupportsStreaming(false);
    setEditNewIsActive(true);
    setEditNewIsDeprecated(false);
    setEditNewDescription('');
    setEditNewDisplayName('');
    setEditNewTags([]);
    setSelectedExistingModel(null);
    setExistingModelSearch('');
    setExistingModelOptions([]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedModel(null);
  };

  const handleSave = async () => {
    if (!selectedModel) return;

    setSaving(true);
    try {
      const updatePayload: Record<string, any> = {};

      if (selectedModel.fluxConversion !== editFluxConversion) {
        updatePayload.fluxConversion = editFluxConversion;
      }

      if (!selectedModel.systemAiModel && editAiModelMode === 'create') {
        if (!editNewAiCompany.trim() || !editNewModelName.trim() || !editNewModelType.trim()) {
          setNotification({
            type: NotificationType.Error,
            message: 'AI Company, Model Name and Model Type are required',
          });
          setSaving(false);
          return;
        }
        updatePayload.createSystemAiModel = true;
        updatePayload.aiCompany = editNewAiCompany;
        updatePayload.modelName = editNewModelName;
        updatePayload.modelType = editNewModelType;
        updatePayload.maxInputToken = editNewMaxInputToken;
        updatePayload.maxOutputToken = editNewMaxOutputToken;
        updatePayload.contextWindow = editNewContextWindow;
        updatePayload.isVisionModel = editNewIsVisionModel;
        updatePayload.supportsReasoning = editNewSupportsReasoning;
        updatePayload.reasoningAlwaysOn = editNewReasoningAlwaysOn;
        updatePayload.supportsWebSearch = editNewSupportsWebSearch;
        updatePayload.supportsStreaming = editNewSupportsStreaming;
        updatePayload.isActive = editNewIsActive;
        updatePayload.isDeprecated = editNewIsDeprecated;
        updatePayload.description = editNewDescription || undefined;
        updatePayload.displayName = editNewDisplayName || undefined;
        updatePayload.tags = editNewTags;
      } else if (!selectedModel.systemAiModel && editAiModelMode === 'existing') {
        if (!selectedExistingModel) {
          setNotification({
            type: NotificationType.Error,
            message: 'Please select an existing System AI Model',
          });
          setSaving(false);
          return;
        }
        updatePayload.systemAiModelId = selectedExistingModel.id;
      }

      if (Object.keys(updatePayload).length > 0) {
        await AdminAiModelsService.updateModelPrice(selectedModel.id, updatePayload);
      }

      if (selectedModel.systemAiModel) {
        const aiModel = selectedModel.systemAiModel;
        const sysUpdates: Record<string, any> = {};

        if (aiModel.maxOutputToken !== editMaxOutputToken)
          sysUpdates.maxOutputToken = editMaxOutputToken;
        if ((aiModel.maxInputToken ?? null) !== editMaxInputToken)
          sysUpdates.maxInputToken = editMaxInputToken;
        if ((aiModel.contextWindow ?? null) !== editContextWindow)
          sysUpdates.contextWindow = editContextWindow;
        if (aiModel.isActive !== editIsActive) sysUpdates.isActive = editIsActive;
        if (aiModel.isDeprecated !== editIsDeprecated)
          sysUpdates.isDeprecated = editIsDeprecated;
        if (aiModel.isVisionModel !== editIsVisionModel)
          sysUpdates.isVisionModel = editIsVisionModel;
        if (aiModel.supportsReasoning !== editSupportsReasoning)
          sysUpdates.supportsReasoning = editSupportsReasoning;
        if (aiModel.reasoningAlwaysOn !== editReasoningAlwaysOn)
          sysUpdates.reasoningAlwaysOn = editReasoningAlwaysOn;
        if (aiModel.supportsWebSearch !== editSupportsWebSearch)
          sysUpdates.supportsWebSearch = editSupportsWebSearch;
        if (aiModel.supportsStreaming !== editSupportsStreaming)
          sysUpdates.supportsStreaming = editSupportsStreaming;
        if ((aiModel.displayName ?? '') !== editDisplayName)
          sysUpdates.displayName = editDisplayName || null;
        if (JSON.stringify(aiModel.tags ?? []) !== JSON.stringify(editTags))
          sysUpdates.tags = editTags;

        if (Object.keys(sysUpdates).length > 0) {
          await AdminAiModelsService.updateSystemAiModel(
            selectedModel.id,
            sysUpdates,
          );
        }
      }

      setNotification({
        type: NotificationType.Success,
        message: 'Model updated successfully',
      });

      handleCloseModal();
      fetchModels();
    } catch (error: any) {
      setNotification({
        type: NotificationType.Error,
        message: error?.message || 'Failed to update model',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreateModal = () => {
    setCreateForm({ ...EMPTY_CREATE_FORM });
    setAiModelMode('create');
    setSelectedExistingModel(null);
    setExistingModelSearch('');
    setExistingModelOptions([]);
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const updateCreateField = <K extends keyof CreateModelPricePayload>(
    field: K,
    value: CreateModelPricePayload[K],
  ) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!createForm.model.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: 'Model price key is required',
      });
      return;
    }
    if (aiModelMode === 'create') {
      if (!createForm.aiCompany?.trim()) {
        setNotification({
          type: NotificationType.Error,
          message: 'AI Company is required',
        });
        return;
      }
      if (!createForm.modelName?.trim()) {
        setNotification({
          type: NotificationType.Error,
          message: 'Model Name is required',
        });
        return;
      }
    }
    if (aiModelMode === 'existing' && !selectedExistingModel) {
      setNotification({
        type: NotificationType.Error,
        message: 'Please select an existing system AI model',
      });
      return;
    }

    setCreating(true);
    try {
      const payload: CreateModelPricePayload = {
        model: createForm.model,
        fluxConversion: createForm.fluxConversion,
      };

      if (aiModelMode === 'create') {
        payload.createSystemAiModel = true;
        payload.aiCompany = createForm.aiCompany;
        payload.modelName = createForm.modelName;
        payload.modelType = createForm.modelType;
        payload.maxInputToken = createForm.maxInputToken;
        payload.maxOutputToken = createForm.maxOutputToken;
        payload.contextWindow = createForm.contextWindow;
        payload.isVisionModel = createForm.isVisionModel;
        payload.supportsReasoning = createForm.supportsReasoning;
        payload.reasoningAlwaysOn = createForm.reasoningAlwaysOn;
        payload.supportsWebSearch = createForm.supportsWebSearch;
        payload.supportsStreaming = createForm.supportsStreaming;
        payload.isActive = createForm.isActive;
        payload.isDeprecated = createForm.isDeprecated;
        payload.description = createForm.description;
        payload.displayName = createForm.displayName;
        payload.tags = createForm.tags;
      } else if (aiModelMode === 'existing') {
        payload.systemAiModelId = selectedExistingModel!.id;
      }

      await AdminAiModelsService.create(payload);
      setNotification({
        type: NotificationType.Success,
        message: 'Model created successfully',
      });
      handleCloseCreateModal();
      fetchModels();
    } catch (error: any) {
      setNotification({
        type: NotificationType.Error,
        message: error?.message || 'Failed to create model',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async () => {
    setSyncConfirmOpen(false);
    setSyncing(true);
    try {
      await AdminAiModelsService.syncTechnologies();
      setNotification({
        type: NotificationType.Success,
        message: 'Technologies synced successfully for all companies',
      });
      fetchModels();
    } catch (error: any) {
      setNotification({
        type: NotificationType.Error,
        message: error?.message || 'Failed to sync technologies',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#3650DE',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Link
            to="/admin"
            style={{ color: '#ffffffaa', textDecoration: 'none', fontSize: 14 }}
          >
            Admin Dashboard
          </Link>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
            AI Models Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={
              syncing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ArrowsClockwise size={18} weight="bold" />
              )
            }
            onClick={() => setSyncConfirmOpen(true)}
            disabled={syncing}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
            }}
          >
            Sync Technologies
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={18} weight="bold" />}
            onClick={handleOpenCreateModal}
            sx={{
              backgroundColor: '#fff',
              color: '#3650DE',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#e8eaff' },
            }}
          >
            Create Model
          </Button>
        </Box>
      </Box>

      <Dialog open={syncConfirmOpen} onClose={() => setSyncConfirmOpen(false)}>
        <DialogTitle>Sync Technologies</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will sync all active, non-deprecated AI models to every company
            in the system. New models will be created for any company that
            doesn't already have them.
            <br />
            <br />
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleSync} variant="contained" autoFocus>
            Sync
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search by model name, AI company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={18} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              size="small"
              label="Active"
              value={filterActive === null ? '' : String(filterActive)}
              onChange={e => {
                const v = e.target.value;
                setFilterActive(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Deprecated"
              value={filterDeprecated === null ? '' : String(filterDeprecated)}
              onChange={e => {
                const v = e.target.value;
                setFilterDeprecated(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Deprecated</MenuItem>
              <MenuItem value="false">Not Deprecated</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Vision"
              value={filterVision === null ? '' : String(filterVision)}
              onChange={e => {
                const v = e.target.value;
                setFilterVision(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Vision</MenuItem>
              <MenuItem value="false">Non-Vision</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Reasoning"
              value={filterReasoning === null ? '' : String(filterReasoning)}
              onChange={e => {
                const v = e.target.value;
                setFilterReasoning(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Reasoning</MenuItem>
              <MenuItem value="false">No Reasoning</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Reasoning Always On"
              value={filterReasoningAlways === null ? '' : String(filterReasoningAlways)}
              onChange={e => {
                const v = e.target.value;
                setFilterReasoningAlways(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Always On</MenuItem>
              <MenuItem value="false">Optional</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Web Search"
              value={filterWebSearch === null ? '' : String(filterWebSearch)}
              onChange={e => {
                const v = e.target.value;
                setFilterWebSearch(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Web Search</MenuItem>
              <MenuItem value="false">No Web Search</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Streaming"
              value={filterStreaming === null ? '' : String(filterStreaming)}
              onChange={e => {
                const v = e.target.value;
                setFilterStreaming(v === '' ? null : v === 'true');
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Streaming</MenuItem>
              <MenuItem value="false">No Streaming</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Model Type"
              value={filterModelType}
              onChange={e => setFilterModelType(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Types</MenuItem>
              {MODEL_TYPES.map(t => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            {(filterActive !== null ||
              filterDeprecated !== null ||
              filterVision !== null ||
              filterReasoning !== null ||
              filterReasoningAlways !== null ||
              filterWebSearch !== null ||
              filterStreaming !== null ||
              filterModelType !== '') && (
              <Button
                size="small"
                onClick={() => {
                  setFilterActive(null);
                  setFilterDeprecated(null);
                  setFilterVision(null);
                  setFilterReasoning(null);
                  setFilterReasoningAlways(null);
                  setFilterWebSearch(null);
                  setFilterStreaming(null);
                  setFilterModelType('');
                }}
                sx={{ textTransform: 'none' }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Model (Price Key)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>AI Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Flux Conversion
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Linked
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Active
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Deprecated
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Vision
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Streaming
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Default
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map(row => {
                    const ai = row.systemAiModel;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOpenModal(row)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.model}
                          </Typography>
                          {ai && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {ai.modelName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {ai?.aiCompany ?? (
                            <Typography variant="caption" color="text.disabled">
                              --
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {ai?.modelType ?? (
                            <Typography variant="caption" color="text.disabled">
                              --
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.fluxConversion}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ai ? 'Yes' : 'No'}
                            size="small"
                            color={ai ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {ai ? (
                            <Chip
                              label={ai.isActive ? 'Yes' : 'No'}
                              size="small"
                              color={ai.isActive ? 'success' : 'default'}
                              variant="outlined"
                            />
                          ) : (
                            '--'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {ai ? (
                            <Chip
                              label={ai.isDeprecated ? 'Yes' : 'No'}
                              size="small"
                              color={ai.isDeprecated ? 'warning' : 'default'}
                              variant="outlined"
                            />
                          ) : (
                            '--'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {ai ? (ai.isVisionModel ? 'Yes' : 'No') : '--'}
                        </TableCell>
                        <TableCell align="center">
                          {ai ? (ai.supportsStreaming ? 'Yes' : 'No') : '--'}
                        </TableCell>
                        <TableCell align="center">
                          {ai?.isDefault ? (
                            <Star size={18} weight="fill" color="#F59E0B" />
                          ) : (
                            '--'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {models.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No models found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </>
        )}
      </Box>

      <KitModal
        show={modalOpen}
        onHide={handleCloseModal}
        title={selectedModel?.model ?? 'Model Details'}
        fullWidth
        maxWidth="md"
        footer={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 1,
            }}
          >
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 100 }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
        }
      >
        {selectedModel && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {selectedModel.systemAiModel && (
              <Box
                sx={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  System AI Model Info
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      AI Company
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.aiCompany}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Model Name
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.modelName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Model Type
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.modelType}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Max Input Tokens
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.maxInputToken ?? 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Max Output Tokens
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.maxOutputToken ?? 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Context Window
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.contextWindow ?? 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Active
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.isActive ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Deprecated
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.isDeprecated ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Vision Model
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.isVisionModel ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Streaming
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.supportsStreaming
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                  </Box>
                </Box>
                {selectedModel.systemAiModel.displayName && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Display Name
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.displayName}
                    </Typography>
                  </Box>
                )}
                {selectedModel.systemAiModel.description && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {selectedModel.systemAiModel.description}
                    </Typography>
                  </Box>
                )}
                {selectedModel.systemAiModel.tags && selectedModel.systemAiModel.tags.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedModel.systemAiModel.tags.map((tag, i) => (
                        <Tooltip key={i} title={tag.tooltip || ''} arrow>
                          <Chip label={tag.label} size="small" variant="outlined" />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {!selectedModel.systemAiModel && (
              <Box
                sx={{
                  backgroundColor: '#FFF8E1',
                  borderRadius: 1,
                  p: 2,
                  border: '1px solid #FFE082',
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  No System AI Model Linked
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This model price does not have a linked System AI Model. You
                  can create a new one or link to an existing one.
                </Typography>
                <RadioGroup
                  row
                  value={editAiModelMode}
                  onChange={e => {
                    setEditAiModelMode(e.target.value as AiModelMode);
                    setSelectedExistingModel(null);
                    setExistingModelSearch('');
                    setExistingModelOptions([]);
                  }}
                >
                  <FormControlLabel
                    value="none"
                    control={<Radio size="small" />}
                    label="No changes"
                  />
                  <FormControlLabel
                    value="create"
                    control={<Radio size="small" />}
                    label="Create new"
                  />
                  <FormControlLabel
                    value="existing"
                    control={<Radio size="small" />}
                    label="Link existing"
                  />
                </RadioGroup>

                {editAiModelMode === 'create' && (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      New System AI Model
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                      }}
                    >
                      <TextField
                        select
                        label="AI Company *"
                        size="small"
                        value={editNewAiCompany}
                        onChange={e => setEditNewAiCompany(e.target.value)}
                      >
                        {AI_COMPANIES.map(c => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Tooltip title="Model name used in API" arrow placement="top">
                        <TextField
                          label="Model Name *"
                          size="small"
                          value={editNewModelName}
                          onChange={e => setEditNewModelName(e.target.value)}
                        />
                      </Tooltip>
                      <TextField
                        select
                        label="Model Type *"
                        size="small"
                        value={editNewModelType}
                        onChange={e => setEditNewModelType(e.target.value)}
                      >
                        {MODEL_TYPES.map(t => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Max Input Tokens"
                        type="number"
                        size="small"
                        value={editNewMaxInputToken ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          setEditNewMaxInputToken(v === '' ? null : parseInt(v) || 0);
                        }}
                      />
                      <TextField
                        label="Max Output Tokens"
                        type="number"
                        size="small"
                        value={editNewMaxOutputToken ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          setEditNewMaxOutputToken(v === '' ? null : parseInt(v) || 0);
                        }}
                      />
                      <TextField
                        label="Context Window"
                        type="number"
                        size="small"
                        value={editNewContextWindow ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          setEditNewContextWindow(v === '' ? null : parseInt(v) || 0);
                        }}
                      />
                    </Box>
                    <TextField
                      label="Display Name"
                      size="small"
                      value={editNewDisplayName}
                      onChange={e => setEditNewDisplayName(e.target.value)}
                    />
                    <TextField
                      label="Description"
                      size="small"
                      multiline
                      minRows={2}
                      value={editNewDescription}
                      onChange={e => setEditNewDescription(e.target.value)}
                    />
                    <TagsEditor tags={editNewTags} onChange={setEditNewTags} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewIsActive}
                            onChange={e => setEditNewIsActive(e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewIsDeprecated}
                            onChange={e => setEditNewIsDeprecated(e.target.checked)}
                          />
                        }
                        label="Deprecated"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewIsVisionModel}
                            onChange={e => setEditNewIsVisionModel(e.target.checked)}
                          />
                        }
                        label="Vision Model"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewSupportsReasoning}
                            onChange={e => setEditNewSupportsReasoning(e.target.checked)}
                          />
                        }
                        label="Supports Reasoning"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewReasoningAlwaysOn}
                            onChange={e => setEditNewReasoningAlwaysOn(e.target.checked)}
                          />
                        }
                        label="Reasoning Always On"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewSupportsWebSearch}
                            onChange={e => setEditNewSupportsWebSearch(e.target.checked)}
                          />
                        }
                        label="Supports Web Search"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editNewSupportsStreaming}
                            onChange={e => setEditNewSupportsStreaming(e.target.checked)}
                          />
                        }
                        label="Supports Streaming"
                      />
                    </Box>
                  </Box>
                )}

                {editAiModelMode === 'existing' && (
                  <Box sx={{ mt: 2 }}>
                    <Autocomplete
                      options={existingModelOptions || []}
                      getOptionLabel={(opt: SystemAiModelResponse) =>
                        `${opt.aiCompany} / ${opt.modelName}`
                      }
                      value={selectedExistingModel}
                      onChange={(_e, val) =>
                        setSelectedExistingModel(val as SystemAiModelResponse | null)
                      }
                      inputValue={existingModelSearch}
                      onInputChange={(_e, val) => setExistingModelSearch(val)}
                      loading={searchingModels}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.aiCompany} / {option.modelName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.modelType}
                              {option.maxOutputToken
                                ? ` | Max output: ${option.maxOutputToken}`
                                : ''}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search System AI Models"
                          size="small"
                          placeholder="Type to search by company or model name..."
                        />
                      )}
                      noOptionsText={
                        searchingModels
                          ? 'Searching...'
                          : existingModelSearch.length < 1
                            ? 'Type to search'
                            : 'No models found'
                      }
                    />
                    {selectedExistingModel && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          backgroundColor: '#F0F7FF',
                          borderRadius: 1,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Model Type
                          </Typography>
                          <Typography variant="body2">
                            {selectedExistingModel.modelType}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Max Output Tokens
                          </Typography>
                          <Typography variant="body2">
                            {selectedExistingModel.maxOutputToken ?? 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Active
                          </Typography>
                          <Typography variant="body2">
                            {selectedExistingModel.isActive ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Editable Fields
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                }}
              >
                <TextField
                  label="Flux Conversion"
                  type="number"
                  size="small"
                  value={editFluxConversion}
                  onChange={e =>
                    setEditFluxConversion(parseFloat(e.target.value) || 0)
                  }
                  inputProps={{ step: 'any' }}
                />

                {selectedModel.systemAiModel && (
                  <>
                    <TextField
                      label="Display Name"
                      size="small"
                      value={editDisplayName}
                      onChange={e => setEditDisplayName(e.target.value)}
                    />

                    <TextField
                      label="Max Output Tokens"
                      type="number"
                      size="small"
                      value={editMaxOutputToken}
                      onChange={e =>
                        setEditMaxOutputToken(parseInt(e.target.value) || 0)
                      }
                    />

                    <TextField
                      label="Max Input Tokens"
                      type="number"
                      size="small"
                      value={editMaxInputToken ?? ''}
                      onChange={e => {
                        const v = e.target.value;
                        setEditMaxInputToken(v === '' ? null : parseInt(v) || 0);
                      }}
                    />

                    <TextField
                      label="Context Window"
                      type="number"
                      size="small"
                      value={editContextWindow ?? ''}
                      onChange={e => {
                        const v = e.target.value;
                        setEditContextWindow(v === '' ? null : parseInt(v) || 0);
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editIsActive}
                          onChange={e => setEditIsActive(e.target.checked)}
                        />
                      }
                      label="Active"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editIsDeprecated}
                          onChange={e => setEditIsDeprecated(e.target.checked)}
                        />
                      }
                      label="Deprecated"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editIsVisionModel}
                          onChange={e => setEditIsVisionModel(e.target.checked)}
                        />
                      }
                      label="Vision Model"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editSupportsReasoning}
                          onChange={e =>
                            setEditSupportsReasoning(e.target.checked)
                          }
                        />
                      }
                      label="Supports Reasoning"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editReasoningAlwaysOn}
                          onChange={e =>
                            setEditReasoningAlwaysOn(e.target.checked)
                          }
                        />
                      }
                      label="Reasoning Always On"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editSupportsWebSearch}
                          onChange={e =>
                            setEditSupportsWebSearch(e.target.checked)
                          }
                        />
                      }
                      label="Supports Web Search"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editSupportsStreaming}
                          onChange={e =>
                            setEditSupportsStreaming(e.target.checked)
                          }
                        />
                      }
                      label="Supports Streaming"
                    />
                  </>
                )}
              </Box>
            </Box>

            {selectedModel.systemAiModel && (
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Default Model
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Set this model as the default for its AI system ({selectedModel.systemAiModel.aiCompany} / {selectedModel.systemAiModel.modelType}). Only one model can be default per system.
                </Typography>
                <Button
                  variant={selectedModel.systemAiModel.isDefault ? 'outlined' : 'contained'}
                  size="small"
                  startIcon={<Star size={16} weight={selectedModel.systemAiModel.isDefault ? 'fill' : 'regular'} />}
                  onClick={async () => {
                    try {
                      await AdminAiModelsService.setDefault(
                        selectedModel.id,
                        !selectedModel.systemAiModel!.isDefault,
                      );
                      setNotification({
                        type: NotificationType.Success,
                        message: selectedModel.systemAiModel!.isDefault
                          ? 'Default removed'
                          : 'Set as default successfully',
                      });
                      handleCloseModal();
                      fetchModels();
                    } catch (error: any) {
                      setNotification({
                        type: NotificationType.Error,
                        message: error?.message || 'Failed to update default',
                      });
                    }
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {selectedModel.systemAiModel.isDefault ? 'Remove Default' : 'Set as Default'}
                </Button>
              </Box>
            )}

            {selectedModel.systemAiModel && (
              <TagsEditor tags={editTags} onChange={setEditTags} />
            )}
          </Box>
        )}
      </KitModal>

      <KitModal
        show={createModalOpen}
        onHide={handleCloseCreateModal}
        title="Create New Model"
        fullWidth
        maxWidth="md"
        footer={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 1,
            }}
          >
            <Button variant="outlined" onClick={handleCloseCreateModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
              sx={{ minWidth: 100 }}
            >
              {creating ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Model Price
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
              }}
            >
              <TextField
                label="Model Price Key *"
                size="small"
                placeholder="e.g. gpt-4o or document_generation"
                value={createForm.model}
                onChange={e => updateCreateField('model', e.target.value)}
                helperText="Unique identifier used for pricing lookups"
              />
              <TextField
                label="Flux Conversion"
                type="number"
                size="small"
                value={createForm.fluxConversion}
                onChange={e =>
                  updateCreateField(
                    'fluxConversion',
                    parseFloat(e.target.value) || 0,
                  )
                }
                inputProps={{ step: 'any' }}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              System AI Model
            </Typography>
            <RadioGroup
              value={aiModelMode}
              onChange={e => {
                setAiModelMode(e.target.value as AiModelMode);
                setSelectedExistingModel(null);
              }}
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label="No AI model (pricing-only entry, e.g. tools or features)"
              />
              <FormControlLabel
                value="create"
                control={<Radio size="small" />}
                label="Create a new system AI model"
              />
              <FormControlLabel
                value="existing"
                control={<Radio size="small" />}
                label="Link to an existing system AI model"
              />
            </RadioGroup>
          </Box>

          {aiModelMode === 'existing' && (
            <Box>
              <Autocomplete
                options={existingModelOptions || []}
                getOptionLabel={opt =>
                  `${opt.aiCompany} / ${opt.modelName} (${opt.modelType})`
                }
                value={selectedExistingModel}
                onChange={(_, value) => setSelectedExistingModel(value)}
                onInputChange={(_, value) => setExistingModelSearch(value)}
                loading={searchingModels}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Search existing AI model"
                    size="small"
                    placeholder="Type to search by company or model name..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchingModels ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.aiCompany} / {option.modelName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.modelType}
                        {option.isDeprecated ? ' (deprecated)' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText={
                  existingModelSearch
                    ? 'No models found'
                    : 'Type to search...'
                }
              />
              {selectedExistingModel && (
                <Box
                  sx={{
                    mt: 1.5,
                    backgroundColor: '#F9FAFB',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        AI Company
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.aiCompany}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Model Name
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.modelName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.modelType}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Active
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.isActive ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Vision
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.isVisionModel ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Streaming
                      </Typography>
                      <Typography variant="body2">
                        {selectedExistingModel.supportsStreaming ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {aiModelMode === 'create' && (
            <>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  System AI Model
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  <TextField
                    select
                    label="AI Company *"
                    size="small"
                    value={createForm.aiCompany}
                    onChange={e => updateCreateField('aiCompany', e.target.value)}
                  >
                    {AI_COMPANIES.map(c => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title="Model name used in API" arrow placement="top">
                    <TextField
                      label="Model Name *"
                      size="small"
                      placeholder="e.g. gpt-4o"
                      value={createForm.modelName}
                      onChange={e => updateCreateField('modelName', e.target.value)}
                    />
                  </Tooltip>
                  <TextField
                    label="Model Type *"
                    size="small"
                    select
                    value={createForm.modelType}
                    onChange={e => updateCreateField('modelType', e.target.value)}
                  >
                    {MODEL_TYPES.map(t => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Max Input Tokens"
                    type="number"
                    size="small"
                    value={createForm.maxInputToken ?? ''}
                    onChange={e =>
                      updateCreateField(
                        'maxInputToken',
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                  />
                  <TextField
                    label="Max Output Tokens"
                    type="number"
                    size="small"
                    value={createForm.maxOutputToken ?? ''}
                    onChange={e =>
                      updateCreateField(
                        'maxOutputToken',
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                  />
                  <TextField
                    label="Context Window"
                    type="number"
                    size="small"
                    value={createForm.contextWindow ?? ''}
                    onChange={e =>
                      updateCreateField(
                        'contextWindow',
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                  />
                  <TextField
                    label="Display Name"
                    size="small"
                    placeholder="e.g. GPT-4o"
                    value={createForm.displayName ?? ''}
                    onChange={e => updateCreateField('displayName', e.target.value)}
                  />
                  <TextField
                    label="Description"
                    size="small"
                    multiline
                    minRows={2}
                    value={createForm.description ?? ''}
                    onChange={e => updateCreateField('description', e.target.value)}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                </Box>
              </Box>

              <TagsEditor
                tags={createForm.tags ?? []}
                onChange={tags => updateCreateField('tags', tags)}
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Capabilities
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.isActive ?? true}
                        onChange={e =>
                          updateCreateField('isActive', e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.isVisionModel ?? false}
                        onChange={e =>
                          updateCreateField('isVisionModel', e.target.checked)
                        }
                      />
                    }
                    label="Supports vision (model capability)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.supportsReasoning ?? false}
                        onChange={e =>
                          updateCreateField('supportsReasoning', e.target.checked)
                        }
                      />
                    }
                    label="Supports Reasoning"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.reasoningAlwaysOn ?? false}
                        onChange={e =>
                          updateCreateField('reasoningAlwaysOn', e.target.checked)
                        }
                      />
                    }
                    label="Reasoning Always On"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.supportsWebSearch ?? false}
                        onChange={e =>
                          updateCreateField('supportsWebSearch', e.target.checked)
                        }
                      />
                    }
                    label="Supports Web Search"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.supportsStreaming ?? false}
                        onChange={e =>
                          updateCreateField('supportsStreaming', e.target.checked)
                        }
                      />
                    }
                    label="Supports Streaming"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createForm.isDeprecated ?? false}
                        onChange={e =>
                          updateCreateField('isDeprecated', e.target.checked)
                        }
                      />
                    }
                    label="Deprecated"
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </KitModal>
    </Box>
  );
};

export default AiModelsManagementPage;
